/**
 * Our own line icons for the deck (never the official card art). Each icon is a list of SVG
 * path strings on a 24×24 grid, stroked with round caps/joins. Rendered via Path2D in
 * cardFace.js and usable as inline SVG via `iconSvg()`.
 */

/** Circle as an SVG path (two arcs). */
const c = (cx, cy, r) => `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0z`;
/** Four-point sparkle centred at (x, y) with radius r. */
const spark = (x, y, r) => {
  const k = r * 0.28;
  return `M${x} ${y - r}L${x + k} ${y - k}L${x + r} ${y}L${x + k} ${y + k}L${x} ${y + r}L${x - k} ${y + k}L${x - r} ${y}L${x - k} ${y - k}z`;
};

/** @type {Record<string, string[]>} */
export const ICONS = {
  plate: [c(12, 12, 8.5), c(12, 12, 5.2), 'M9.2 9.4a3.8 3.8 0 0 1 2.2-1.5'],
  shirt: [
    'M8 3.5 4 5.5 2.5 10l3 1.2v9.3h13v-9.3l3-1.2L20 5.5l-4-2c-.6 1.6-2.2 2.6-4 2.6S8.6 5.1 8 3.5z',
  ],
  hanger: ['M10 5.5a2 2 0 1 1 2 2V9', 'M12 9 2.9 16.2c-.7.6-.3 1.8.6 1.8h17c.9 0 1.3-1.2.6-1.8z'],
  spray: [
    'M8 9h7l1.5 3v8.5a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1V12z',
    'M9 9V5.5h5l2 1.5',
    'M14 5.5h3',
    'M19.5 4.2v.01M19.5 7.2v.01M21.5 5.7v.01',
  ],
  trash: ['M4 6.5h16', 'M9.5 6.5V4h5v2.5', 'M6 6.5l1 13.5h10l1-13.5', 'M10 10.5v6M14 10.5v6'],
  cart: ['M2.5 4h2.6l2.4 10.5h10.6L20.8 7.5H6', c(9, 19, 1.4), c(16.5, 19, 1.4)],
  sofa: [
    'M5 11V8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3',
    'M3 12.5a1.5 1.5 0 0 1 3 0V15h12v-2.5a1.5 1.5 0 0 1 3 0V18H3z',
    'M5 18v2M19 18v2',
  ],
  box: [
    'M3.5 7.5 12 3.5l8.5 4v9L12 20.5l-8.5-4z',
    'M3.5 7.5 12 11.5l8.5-4',
    'M12 11.5v9',
    'M7.8 5.5l8.4 4',
  ],
  wrench: [
    'M14.5 3.5a4.5 4.5 0 0 0-4.2 6.1l-6.5 6.5a1.9 1.9 0 0 0 2.7 2.7l6.5-6.5a4.5 4.5 0 0 0 6.1-4.2l-2.6 2.6-2.4-.6-.6-2.4z',
  ],
  house: ['M3.5 11 12 4l8.5 7', 'M5.5 9.5V20h13V9.5', 'M10 20v-5h4v5'],
  houseKey: [
    'M3.5 11 12 4l8.5 7',
    'M5.5 9.5V20h13V9.5',
    c(12, 12.5, 1.8),
    'M12 14.3V18M12 16.5h1.5',
  ],
  glasses: [
    'M4.5 3h5.5l-.5 5a2.25 2.25 0 0 1-4.5 0z',
    'M7.25 10.2V19M5 19h4.5',
    'M14 3h5.5L19 8a2.25 2.25 0 0 1-4.5 0z',
    'M16.75 10.2V19M14.5 19H19',
  ],
  plant: [
    'M7 14h10l-1.3 6.5H8.3z',
    'M12 14V8',
    'M12 9.5C12 6.5 9.5 4.5 6.5 4.5c0 3 2.5 5 5.5 5z',
    'M12 8c0-2.5 2-4.5 5.5-4.5 0 2.8-2.2 4.5-5.5 4.5z',
  ],
  envelope: ['M3.5 6h17v12h-17z', 'M3.5 6.5 12 13l8.5-6.5'],
  postcard: ['M3 5.5h18v13H3z', 'M12 8v8', 'M15 8.5h3v3h-3z', 'M5.5 11h4M5.5 13.5h4'],
  apple: [
    'M12 7.5c-1.5-1-5.5-1.2-6.5 2.5-.9 3.4 1.5 8.5 4 9.5 1 .4 1.7-.3 2.5-.3s1.5.7 2.5.3c2.5-1 4.9-6.1 4-9.5-1-3.7-5-3.5-6.5-2.5z',
    'M12 7.5c0-2 .8-3.5 2.5-4',
  ],
  bowl: ['M3.5 11.5h17c0 4.4-3.8 8-8.5 8s-8.5-3.6-8.5-8z', 'M14.5 11.5 19 4.5'],
  pot: [
    'M4.5 10h15v7.5a2.5 2.5 0 0 1-2.5 2.5H7a2.5 2.5 0 0 1-2.5-2.5z',
    'M2.5 10h19',
    'M9 3.5c-.8 1 .8 2-.1 3M12 3c-.8 1 .8 2-.1 3M15 3.5c-.8 1 .8 2-.1 3',
  ],
  pan: [c(9.5, 12.5, 6), 'M15.5 12.5h6', c(9.5, 12.5, 2.2)],
  camera: ['M3.5 8h4L9 5.5h6L16.5 8h4v11.5h-17z', c(12, 13.5, 3.5)],
  coins: [
    'M12 4.5c4.1 0 7.5 1.3 7.5 3s-3.4 3-7.5 3-7.5-1.3-7.5-3 3.4-3 7.5-3z',
    'M4.5 7.5V12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3V7.5',
    'M4.5 12v4.5c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3V12',
  ],
  banknote: ['M2.5 7h19v10h-19z', c(12, 12, 2.6), 'M5.5 10v4M18.5 10v4'],
  car: [
    'M3.5 16.5v-4l2-5a1.5 1.5 0 0 1 1.4-1h10.2a1.5 1.5 0 0 1 1.4 1l2 5v4z',
    'M3.5 12.5h17',
    c(7.5, 17, 1.8),
    c(16.5, 17, 1.8),
  ],
  cake: [
    'M4.5 20.5v-7h15v7z',
    'M3 20.5h18',
    'M4.5 16.5c1.5 1.2 3 1.2 4.5 0s3-1.2 4.5 0 3 1.2 4.5 0',
    'M8 13.5v-3M12 13.5v-3M16 13.5v-3',
    'M8 8.5c-.6-.6-.6-1.6 0-2.5.6.9.6 1.9 0 2.5zM12 8.5c-.6-.6-.6-1.6 0-2.5.6.9.6 1.9 0 2.5zM16 8.5c-.6-.6-.6-1.6 0-2.5.6.9.6 1.9 0 2.5z',
  ],
  calendar: [
    'M4 6h16v14.5H4z',
    'M4 10h16',
    'M8.5 3.5V8M15.5 3.5V8',
    'M8 13.5h.01M12 13.5h.01M16 13.5h.01M8 17h.01M12 17h.01',
  ],
  handHeart: [
    'M12 11.5c-1.6-1.4-4-2.5-4-4.6a2 2 0 0 1 4-.4 2 2 0 0 1 4 .4c0 2.1-2.4 3.2-4 4.6z',
    'M3 14.5h3l3.5 1.5H13a1.3 1.3 0 0 1 0 2.6H9.5',
    'M13 18.6l5.5-2.4a1.4 1.4 0 0 1 1.5 2.3L14 21.5H6.5L3 20',
  ],
  columns: ['M3.5 9 12 4l8.5 5z', 'M4.5 20.5h15', 'M6.5 11v7M10 11v7M14 11v7M17.5 11v7'],
  laptop: ['M5 6.5h14V16H5z', 'M2.5 18.5h19l-1-2.5h-17z'],
  music: ['M9 18V5.5l10-2V16', c(6.8, 18, 2.2), c(16.8, 16, 2.2)],
  ball: [
    c(12, 12, 8.5),
    'M3.5 12h17',
    'M12 3.5v17',
    'M6 6c2.3 1.9 2.3 10.1 0 12',
    'M18 6c-2.3 1.9-2.3 10.1 0 12',
  ],
  firstAid: ['M4 7.5h16v12H4z', 'M9 7.5V5h6v2.5', 'M12 10.5v6M9 13.5h6'],
  suitcase: ['M4 8h16v11.5H4z', 'M9 8V5.5h6V8', 'M8 8v11.5M16 8v11.5'],
  ticket: [
    'M3.5 7.5h17v3a1.5 1.5 0 0 0 0 3v3h-17v-3a1.5 1.5 0 0 0 0-3z',
    'M14.5 7.5V9M14.5 11.25v1.5M14.5 15v1.5',
  ],
  receipt: [
    'M6 3.5h12v17l-2-1.3-2 1.3-2-1.3-2 1.3-2-1.3-2 1.3z',
    'M15 11.5H9.5M11.5 9.5l-2 2 2 2',
    'M9 7h6',
  ],
  sun: [
    c(12, 12, 4),
    'M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4',
  ],
  snowflake: [
    'M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9',
    'M9.5 4.5 12 6.5l2.5-2M9.5 19.5 12 17.5l2.5 2',
  ],
  clipboard: ['M7 4.5H5.5v16h13v-16H17', 'M9 3h6v3H9z', 'M8.5 11h7M8.5 14.5h7M8.5 18h4'],
  pencil: ['M4 20l1-4.5L15.5 5a2.1 2.1 0 0 1 3 3L8 18.5z', 'M13.5 7l3 3'],
  plane: [
    'M21 4.5c-.8-.8-2.2-.6-3 .2l-3.8 3.8-8.7-2.3L4 7.7l7 3.8-3.2 3.4-2.5-.4-1.2 1.2 3.2 1.8 1.8 3.2 1.2-1.2-.4-2.5 3.4-3.2 3.8 7 1.5-1.5-2.3-8.7 3.8-3.8c.8-.8 1-2.2.2-3z',
  ],
  book: [
    'M12 6.5c-2-1.5-5-2-8.5-1.5v13.5c3.5-.5 6.5 0 8.5 1.5 2-1.5 5-2 8.5-1.5V5c-3.5-.5-6.5 0-8.5 1.5z',
    'M12 6.5V20',
  ],
  horn: ['M12 2.5 8.5 20.5h7z', 'M9.4 16.4l5.4-2.2M10.2 12.4l3.8-1.6M11 8.5l2.3-1'],
  bath: [
    'M3 12h18v2.5a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5z',
    'M5.5 12V6a2 2 0 0 1 3.8-.9',
    'M7 19.5 6 21M17 19.5l1 1.5',
    c(14, 8, 1.3),
    c(17.5, 5.5, 1),
  ],
  bed: ['M3 19V6', 'M3 15h18v4', 'M21 15v-3a3 3 0 0 0-3-3h-7v6', c(7, 11.5, 1.8)],
  moon: ['M19.5 14.5a8 8 0 0 1-10-10 8 8 0 1 0 10 10z', spark(17, 5.5, 2)],
  tooth: [
    'M7.5 3.5c1.8 0 2.7 1 4.5 1s2.7-1 4.5-1c2.5 0 3.5 2 3.5 4.5 0 3-1.5 4.5-2 7-.4 2-1 5.5-2.5 5.5s-1.8-4-3.5-4-2 4-3.5 4-2.1-3.5-2.5-5.5c-.5-2.5-2-4-2-7C4 5.5 5 3.5 7.5 3.5z',
  ],
  bottle: [
    'M9 8.5h6v11a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 19.5z',
    'M8 8.5h8',
    'M10 8.5v-2h4v2',
    'M11 6.5c0-2 .5-3 1-3s1 1 1 3',
    'M9 12.5h2.5M9 15.5h2.5',
  ],
  shieldCheck: [
    'M12 3 4.5 6v5.5c0 4.5 3.2 8 7.5 9.5 4.3-1.5 7.5-5 7.5-9.5V6z',
    'M8.8 12.2 11 14.4l4.2-4.4',
  ],
  shieldCross: ['M12 3 4.5 6v5.5c0 4.5 3.2 8 7.5 9.5 4.3-1.5 7.5-5 7.5-9.5V6z', 'M12 9v6M9 12h6'],
  chat: ['M3.5 5h11.5v8H8.5l-3 2.5V13h-2z', 'M17.5 9h3v8H19v2.5L16 17h-5.5v-1.5'],
  paw: [
    'M8 17.5c0-2.5 2-4.5 4-4.5s4 2 4 4.5c0 1.5-1.2 2.5-2.5 2.5-.6 0-1-.3-1.5-.3s-.9.3-1.5.3C9.2 20 8 19 8 17.5z',
    c(6, 11, 1.6),
    c(9.5, 7.5, 1.6),
    c(14.5, 7.5, 1.6),
    c(18, 11, 1.6),
  ],
  sunrise: [
    'M3 18h18',
    'M7 18a5 5 0 0 1 10 0',
    'M12 9V3.5M10 5.5l2-2 2 2',
    'M4.5 12.5l1.8 1.2M19.5 12.5l-1.8 1.2',
    'M6 21h12',
  ],
  people: [
    c(8.5, 8, 3),
    'M3 19.5c0-3.3 2.5-6 5.5-6s5.5 2.7 5.5 6',
    c(16.5, 9, 2.5),
    'M15 13.7c.5-.2 1-.2 1.5-.2 2.5 0 4.5 2.5 4.5 5.5',
  ],
  school: [
    'M3.5 10.5 12 5l8.5 5.5',
    'M5.5 9.5V20h13V9.5',
    'M10 20v-4.5h4V20',
    'M12 5V2.5h3V4h-3',
    c(12, 10.5, 1.4),
  ],
  backpack: ['M6.5 9.5a5.5 5.5 0 0 1 11 0v11h-11z', 'M9.5 4.3V3h5v1.3', 'M9 14h6v3.5H9z'],
  lotus: [
    'M12 19.5c-3-2-4.5-5-4.5-8 0-2.5 2-5.5 4.5-7 2.5 1.5 4.5 4.5 4.5 7 0 3-1.5 6-4.5 8z',
    'M12 19.5c-4 0-8.5-2.5-9-7 2 0 3.5.4 5 1.3',
    'M12 19.5c4 0 8.5-2.5 9-7-2 0-3.5.4-5 1.3',
  ],
  eye: ['M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z', c(12, 12, 2.8)],
  mind: [
    'M14 21v-3h2.5a2 2 0 0 0 2-2v-2.5l1.5-.5-1.5-3C18.5 6 15.5 3 11.5 3 7.4 3 4.5 6.2 4.5 10c0 2.6 1.3 4.6 3 5.8V21',
    'M11.5 12.5c-1-.9-2.6-1.6-2.6-3a1.3 1.3 0 0 1 2.6-.3 1.3 1.3 0 0 1 2.6.3c0 1.4-1.6 2.1-2.6 3z',
  ],
  question: [
    'M4 5h16v11h-8l-4 3.5V16H4z',
    'M10 8.8a2 2 0 1 1 2.8 1.8c-.5.3-.8.7-.8 1.2v.4',
    'M12 13.6h.01',
  ],
  phone: ['M7.5 2.5h9v19h-9z', 'M11 18.5h2'],
  kite: [
    'M12 2.5 18.5 9 12 15.5 5.5 9z',
    'M5.5 9h13M12 2.5v13',
    'M12 15.5c-1.5 1.5 1.5 2.5 0 4s0 2 0 2',
  ],
  heart: ['M12 20s-8-4.7-8-10.5A4.2 4.2 0 0 1 12 7a4.2 4.2 0 0 1 8 2.5C20 15.3 12 20 12 20z'],
  rings: [c(9, 14.5, 5), c(15, 14.5, 5), 'M10.5 3.5h3l1 1.5-2.5 2.5L9.5 5z'],
  gift: [
    'M3.5 8.5h17V12h-17z',
    'M5 12v8.5h14V12',
    'M12 8.5v12',
    'M12 8.5C10.5 5 7 4.5 7 6.5s3 2 5 2z',
    'M12 8.5c1.5-3.5 5-4 5-2s-3 2-5 2z',
  ],
  tree: ['M12 3.5 6.5 10h3l-4 5.5h4L6.5 19.5h11l-3-4h4l-4-5.5h3z', 'M12 19.5V22'],
  wand: [
    'M4 20 15 9',
    'M13.5 7.5l3 3',
    spark(18.5, 4.5, 2.5),
    spark(8.5, 5.5, 1.6),
    spark(19.5, 13.5, 1.6),
  ],
  whistle: [c(8, 14.5, 5), 'M8 9.5h12.5V13l-7.6 1.6', 'M8 6.5V3.5'],
  hand: [
    'M8 13V5.5a1.5 1.5 0 0 1 3 0V11M11 10.5V4a1.5 1.5 0 0 1 3 0v6.5M14 10.5v-5a1.5 1.5 0 0 1 3 0V14c0 4-2.5 7-6 7-2.5 0-3.8-1-5-3l-2.5-4.2a1.4 1.4 0 0 1 2.3-1.6L8 14',
  ],
  candle: [
    'M9 11h6v10H9z',
    'M12 11V9.2',
    'M12 8.6c-1.3-.8-1.8-2.2-.6-4 .2 1 .9 1.3 1.3 2 .5.8 0 1.6-.7 2z',
  ],
  seedling: [
    'M12 21v-9',
    'M12 12c0-3.5-2.5-6-6.5-6 0 3.8 2.5 6 6.5 6z',
    'M12 14.5c0-3.5 2.5-6 6.5-6 0 3.8-2.5 6-6.5 6z',
    'M7.5 21h9',
  ],
  bolt: ['M13.5 2.5 5 13.5h6l-1 8 8.5-11h-6z'],
  undo: ['M9 14 4 9l5-5', 'M4 9h10.5a5.5 5.5 0 0 1 0 11H11'],
  truck: ['M2.5 6h11v10h-11z', 'M13.5 9.5h4l3 3.5v3h-7', c(6.5, 17.5, 1.8), c(16.5, 17.5, 1.8)],
  briefcase: ['M3.5 8h17v11h-17z', 'M9 8V5.5h6V8', 'M3.5 13h17'],
  pulse: ['M3 12h4l2-4.5 3 9 2.2-6 1.3 1.5H21'],
  baby: [
    c(12, 13, 7),
    'M9.5 12.5h.01M14.5 12.5h.01',
    'M10 15.8c1.2.9 2.8.9 4 0',
    'M12 6c0-1.5 1-2.5 2.2-2.5',
  ],
  comb: ['M3.5 8h17v4h-17z', 'M5.5 12v6M8 12v6M10.5 12v6M13 12v6M15.5 12v6M18 12v6'],
  // Suit emblems.
  compass: [c(12, 12, 8.5), 'M15.5 8.5l-2 5-5 2 2-5z'],
  sparkle: [spark(11, 12.5, 8), spark(19, 5, 2.5)],
  // Markers.
  cup: [
    'M5 9h11v5.5a4.5 4.5 0 0 1-4.5 4.5h-2A4.5 4.5 0 0 1 5 14.5z',
    'M16 10.5h1.5a2.25 2.25 0 0 1 0 4.5H16',
    'M8.5 3.5c-.7.9.7 1.6 0 2.5M12 3.5c-.7.9.7 1.6 0 2.5',
    'M4 21.5h13',
  ],
  heartSmile: [
    'M12 20.5s-8.5-4.9-8.5-11A4.4 4.4 0 0 1 12 7.2a4.4 4.4 0 0 1 8.5 2.3c0 6.1-8.5 11-8.5 11z',
    'M9.2 12.2c1.5 1.6 4.1 1.6 5.6 0',
  ],
  lock: ['M6 11h12v9.5H6z', 'M8.5 11V8a3.5 3.5 0 0 1 7 0v3'],
};

/** Card slug (or pairKey) → icon name. */
const CARD_ICONS = {
  'childcare-helpers-kids': 'people',
  cleaning: 'spray',
  dishes: 'plate',
  'dry-cleaning': 'hanger',
  garbage: 'trash',
  groceries: 'cart',
  'home-furnishings': 'sofa',
  'home-goods-and-supplies': 'box',
  'home-maintenance': 'wrench',
  'home-purchase-rental-mortgage-and-insurance': 'houseKey',
  hosting: 'glasses',
  laundry: 'shirt',
  'lawn-and-plants': 'plant',
  mail: 'envelope',
  'meals-kids-school-lunch': 'apple',
  'meals-weekday-breakfast': 'bowl',
  'meals-weekday-dinner': 'pot',
  'meals-weekend': 'pan',
  'memories-and-photos': 'camera',
  'money-manager': 'coins',
  'storage-garage-and-seasonal-items': 'box',
  'tidying-up-organizing-and-donations': 'spray',
  auto: 'car',
  'birthday-celebrations-other-kids': 'gift',
  'calendar-keeper': 'calendar',
  'cash-and-bills': 'banknote',
  'charity-community-service-and-good-deeds': 'handHeart',
  'civic-engagement-and-cultural-enrichment': 'columns',
  'electronics-and-it': 'laptop',
  'extracurricular-kids-non-sports': 'music',
  'extracurricular-kids-sports': 'ball',
  'first-aid-safety-and-emergency': 'firstAid',
  'packing-and-unpacking-kids-local': 'backpack',
  'packing-and-unpacking-travel': 'suitcase',
  'points-miles-and-coupons': 'ticket',
  'returns-and-store-credit': 'receipt',
  'school-breaks-kids-non-summer': 'snowflake',
  'school-breaks-kids-summer': 'sun',
  'school-forms-kids': 'clipboard',
  'social-plans-couples': 'glasses',
  'transportation-kids': 'car',
  travel: 'plane',
  'tutoring-and-coaching': 'book',
  'weekend-plans': 'calendar',
  'bathing-and-grooming-kids': 'bath',
  'bedtime-routine-kids': 'bed',
  'clothes-and-accessories-kids': 'shirt',
  'dental-kids': 'tooth',
  'diapering-and-potty-training-kids': 'bottle',
  'estate-planning-and-life-insurance': 'shieldCheck',
  'family-planning-birth-control': 'heart',
  'friendship-and-social-media-kids': 'chat',
  'grooming-and-wardrobe': 'comb',
  'health-insurance': 'shieldCross',
  'homework-projects-and-school-supplies-kids': 'pencil',
  'medical-and-healthy-living-kids': 'firstAid',
  'morning-routine': 'sunrise',
  'parents-and-in-laws': 'people',
  pets: 'paw',
  'school-service-kids': 'school',
  'school-transitions-kids': 'backpack',
  'self-care': 'lotus',
  'special-needs-and-mental-health-kids': 'mind',
  'teacher-communication-kids': 'chat',
  'adult-friendships': 'glasses',
  'birthday-celebration-your-kids': 'cake',
  'discipline-and-screen-time-kids': 'phone',
  'extended-family': 'people',
  'fun-and-playing-kids': 'kite',
  'gestures-of-love-kids': 'heart',
  'gifts-family': 'gift',
  'gifts-vip': 'gift',
  'hard-question-kids': 'question',
  'holiday-cards': 'postcard',
  holidays: 'tree',
  'informal-education-kids': 'book',
  'magical-beings-kids': 'wand',
  'marriage-and-romance': 'rings',
  'middle-of-the-night-comfort-kids': 'moon',
  'partner-coach': 'whistle',
  'showing-up-and-participating-kids': 'hand',
  spirituality: 'candle',
  'thank-you-notes': 'envelope',
  'values-and-good-deeds-kids': 'seedling',
  'watching-kids': 'eye',
  'aging-ailing-parent': 'people',
  death: 'candle',
  'first-year-of-infants-life': 'baby',
  'glitch-in-the-matrix-daily-disruption': 'bolt',
  'home-renovation': 'wrench',
  'job-loss-and-money-problems': 'briefcase',
  moving: 'truck',
  'new-job': 'briefcase',
  'serious-illness': 'pulse',
  'welcoming-a-child-into-the-home': 'baby',
  'unicorn-space': 'horn',
};

export const SUIT_ICONS = {
  home: 'house',
  out: 'compass',
  caregiving: 'heart',
  magic: 'sparkle',
  wild: 'bolt',
  unicorn: 'horn',
};

/**
 * @param {{ id: string, pairKey?: string | null, suit: string }} card
 * @returns {string} icon name in ICONS
 */
export function iconFor(card) {
  return (
    CARD_ICONS[card.pairKey ?? ''] ?? CARD_ICONS[card.id] ?? SUIT_ICONS[card.suit] ?? 'sparkle'
  );
}

/** @type {Map<string, Path2D[]>} */
const pathCache = new Map();

/** @param {string} name */
export function iconPaths(name) {
  let paths = pathCache.get(name);
  if (!paths) {
    paths = (ICONS[name] ?? ICONS.sparkle).map((d) => new Path2D(d));
    pathCache.set(name, paths);
  }
  return paths;
}

/**
 * Strokes an icon into a canvas at (x, y) with the given size (in current ctx units).
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} name
 * @param {number} x
 * @param {number} y
 * @param {number} size
 * @param {{ color: string, weight?: number, fill?: string }} style
 */
export function drawIcon(ctx, name, x, y, size, { color, weight = 1.5, fill }) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / 24, size / 24);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = weight;
  ctx.strokeStyle = color;
  for (const p of iconPaths(name)) {
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fill(p);
    }
    ctx.stroke(p);
  }
  ctx.restore();
}

/**
 * Inline SVG markup for an icon (for HTML UI: chips, buttons, panels).
 * @param {string} name
 * @param {{ size?: number, weight?: number, label?: string }} [opts]
 */
export function iconSvg(name, { size = 20, weight = 1.6, label } = {}) {
  const a11y = label ? `role="img" aria-label="${label}"` : 'aria-hidden="true"';
  const d = (ICONS[name] ?? ICONS.sparkle).map((p) => `<path d="${p}"/>`).join('');
  return `<svg ${a11y} width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${weight}" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
}
